/**
 * useSectionState - React hook for section state management
 * Extracted from class to fix hooks-in-class issue
 */

import { useState, useEffect, useCallback } from 'react';
import { sectionStateManager, SectionState, SectionStateHookReturn } from '../state/SectionStateManager';

/**
 * React hook for section state management
 */
export function useSectionState(chartId: string, sectionType: string): SectionStateHookReturn {
    const [state, setState] = useState<SectionState | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load initial state
    useEffect(() => {
        if (!chartId) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            const loadedState = sectionStateManager.loadSectionState(chartId, sectionType);
            setState(loadedState);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load state');
        } finally {
            setIsLoading(false);
        }
    }, [chartId, sectionType]);

    // Accordion state management
    const saveAccordionState = useCallback((expandedSections: Set<string>) => {
        if (!chartId) return;

        try {
            sectionStateManager.saveAccordionState(chartId, sectionType, expandedSections);
            setState(prev => prev ? {
                ...prev,
                accordionSections: Array.from(expandedSections),
                lastUpdated: Date.now()
            } : null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save accordion state');
        }
    }, [chartId, sectionType]);

    const loadAccordionState = useCallback((): Set<string> => {
        return new Set(state?.accordionSections || []);
    }, [state]);

    // Toggle state management
    const saveToggleState = useCallback((toggleId: string, value: boolean) => {
        if (!chartId) return;

        try {
            sectionStateManager.saveToggleState(chartId, sectionType, toggleId, value);
            setState(prev => prev ? {
                ...prev,
                toggleStates: {
                    ...prev.toggleStates,
                    [toggleId]: value
                },
                lastUpdated: Date.now()
            } : null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save toggle state');
        }
    }, [chartId, sectionType]);

    const getToggleState = useCallback((toggleId: string, defaultValue = false): boolean => {
        return state?.toggleStates[toggleId] ?? defaultValue;
    }, [state]);

    // Table state management
    const saveTableState = useCallback((tableId: string, tableState: any) => {
        if (!chartId) return;

        try {
            sectionStateManager.saveTableState(chartId, sectionType, tableId, tableState);
            setState(prev => prev ? {
                ...prev,
                tableStates: {
                    ...prev.tableStates,
                    [tableId]: tableState
                },
                lastUpdated: Date.now()
            } : null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save table state');
        }
    }, [chartId, sectionType]);

    const getTableState = useCallback((tableId: string, defaultState?: any): any => {
        return state?.tableStates[tableId] ?? defaultState;
    }, [state]);

    // Scroll position management
    const saveScrollPosition = useCallback((sectionId: string, position: number) => {
        if (!chartId) return;

        try {
            sectionStateManager.saveScrollPosition(chartId, sectionType, sectionId, position);
            setState(prev => prev ? {
                ...prev,
                scrollPositions: {
                    ...prev.scrollPositions,
                    [sectionId]: position
                },
                lastUpdated: Date.now()
            } : null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save scroll position');
        }
    }, [chartId, sectionType]);

    const getScrollPosition = useCallback((sectionId: string): number => {
        return state?.scrollPositions[sectionId] ?? 0;
    }, [state]);

    // Utilities
    const clearState = useCallback(() => {
        if (!chartId) return;

        try {
            sectionStateManager.clearSectionState(chartId, sectionType);
            setState(prev => prev ? {
                accordionSections: [],
                toggleStates: {},
                tableStates: {},
                scrollPositions: {},
                lastUpdated: Date.now(),
                version: '1.0.0',
                hasInteracted: false
            } : null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to clear state');
        }
    }, [chartId, sectionType]);

    const exportState = useCallback((): SectionState | null => {
        return state;
    }, [state]);

    const importState = useCallback((importedState: SectionState) => {
        if (!chartId) return;

        try {
            sectionStateManager.saveSectionState(chartId, sectionType, importedState);
            setState(importedState);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to import state');
        }
    }, [chartId, sectionType]);

    return {
        state,
        isLoading,
        error,
        saveAccordionState,
        loadAccordionState,
        saveToggleState,
        getToggleState,
        saveTableState,
        getTableState,
        saveScrollPosition,
        getScrollPosition,
        clearState,
        exportState,
        importState
    };
}

export default useSectionState;